import { Button, Modal } from 'antd'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { getCookie } from 'cookies-next'
import styled from 'styled-components'
import { useTranslation } from 'next-i18next'

import { Model } from '@/types/models'
import { GENDER, Gender } from '@/constants/gender'
import { poppins } from '@/components/common/fonts'
import { VoiceSelector } from '@/components/common/voice-selector/LegacyVoiceSelector'
import { isSameLanguage } from '@/lib/language-code-utils'

export const VoiceSelectorModal: FC<{
  isOpened: boolean
  onClose: () => void
  onSelect: (voice: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }) => void

  voice: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }

  ttsList: Tts[]
  models: Model[]
}> = ({
  isOpened,
  onClose,
  onSelect,

  voice,

  ttsList,
  models,
}) => {
  const { t } = useTranslation('common')

  const locale = (getCookie('NEXT_LOCALE') || 'en') as string

  const originalVoice = useMemo(() => {
    const language = voice.language === 'multi' ? locale : voice.language

    const foundModel = models.find((model) =>
      isSameLanguage(model.languageSupport[0], language),
    )

    if (!foundModel) {
      return
    }

    return {
      modelId: foundModel.id,
      voiceId: `deepbrainai/${foundModel.languageSupport[0]}/${foundModel.id}`,
      language: foundModel.languageSupport[0],
      gender: foundModel.tags?.includes('[sex1]') ? GENDER.MALE : GENDER.FEMALE,
      modelName: foundModel.label[locale] || foundModel.label.en,
      sampleUrl: foundModel.clothes[0]?.avatarVideoPath ?? '',
    }
  }, [models, voice, locale])

  const [selectedVoice, setSelectedVoice] = useState(voice)

  useEffect(() => {
    if (!isOpened) {
      return
    }

    setSelectedVoice(voice)
  }, [voice, isOpened])

  const handleSelectVoice = useCallback(
    (voiceId: string) => {
      if (voiceId === originalVoice?.voiceId) {
        setSelectedVoice({
          isOriginalVoice: true,
          voiceId,
          modelId: originalVoice.modelId,
          language: originalVoice.language,
          gender: originalVoice.gender,
          type: 'deepbrainai',
        })
        return
      }

      const foundTts = ttsList.find((_voice) => _voice.id === voiceId)

      if (!!foundTts) {
        setSelectedVoice({
          isOriginalVoice: false,
          voiceId,
          modelId: foundTts.model,
          gender: (voice.gender?.toUpperCase() as Gender) ?? GENDER.MALE,
          language: foundTts.language,
          type: foundTts.type,
        })

        return
      }
    },
    [originalVoice, ttsList, voice],
  )

  const ensuredSelectedVoice = useMemo(() => {
    if (!selectedVoice) {
      return
    }

    if (selectedVoice.isOriginalVoice) {
      return selectedVoice
    }

    const foundTtsVoice = ttsList.find(
      (_voice) => _voice.id === selectedVoice.voiceId,
    )

    if (!!foundTtsVoice) {
      return selectedVoice
    }
  }, [selectedVoice, ttsList])

  const handleApplyVoice = useCallback(() => {
    if (!ensuredSelectedVoice) {
      return
    }

    /**
     * 동일한 음성이 선택되었을 경우 그냥 닫기
     */
    if (ensuredSelectedVoice.voiceId === voice.voiceId) {
      onClose()

      return
    }

    onSelect(ensuredSelectedVoice)
    onClose()
  }, [ensuredSelectedVoice, onSelect, onClose, voice.voiceId])

  return (
    <Modal
      centered
      open={isOpened}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={940}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <ModalContainer>
        <ModalContent>
          <VoiceSelector
            originalVoice={originalVoice}
            voices={ttsList}
            voiceId={selectedVoice.voiceId}
            initialLanguage={voice.language}
            initialGender={voice.gender}
            onSelectVoice={handleSelectVoice}
          />
        </ModalContent>

        <ModalFooter>
          <ApplyVoiceButton
            type="primary"
            disabled={!ensuredSelectedVoice}
            onClick={handleApplyVoice}
          >
            {t('Apply Voice')}
          </ApplyVoiceButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;

  height: calc(100vh - 90px);
  min-height: 400px;
  max-height: 800px;
`

const ModalContent = styled.div`
  flex: 1;

  padding: 25px 25px 0 25px;

  overflow: hidden;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 10px;

  padding: 10px 25px;

  border-top: 1px solid #f3f3f3;
`

const ApplyVoiceButton = styled(Button)`
  width: 160px;
  height: 40px;

  background-color: #4276ee;

  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  color: #ffffff;
`
